import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";
import MarkdownPreview from "@uiw/react-markdown-preview";
import { useTemplate, useUpdateTemplate } from "../../hooks/useTemplates";
import AuditInfo from "../../components/ui/AuditInfo";
import { Input, Textarea, Select } from "../../components/ui/Input";
import { auditApi } from "../../services/api";

export default function EditTemplatePage() {
  const { templateId } = useParams<{ templateId: string }>();
  const id = Number(templateId);
  const navigate = useNavigate();

  const { data: template, isLoading, isError } = useTemplate(id);
  const updateMutation = useUpdateTemplate();

  const [name, setName] = useState("");
  const [templateType, setTemplateType] = useState<"email" | "sms" | string>(
    "email",
  );
  const [subject, setSubject] = useState("");
  const [providerCode, setProviderCode] = useState("");
  const [placeholderParamsText, setPlaceholderParamsText] = useState("{}");
  const [delayedMinutes, setDelayedMinutes] = useState<number | "">("");
  const [contentSyntax, setContentSyntax] = useState<"markdown" | "html">(
    "markdown",
  );
  const [content, setContent] = useState("");

  const [messagePreview, setMessagePreview] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setTemplateType(template.template_type);
      setSubject(template.subject || "");
      setProviderCode(template.provider_template_code || "");
      setPlaceholderParamsText(
        JSON.stringify(template.placeholder_params || {}, null, 2),
      );
      setDelayedMinutes(template.delayed_send_minutes ?? "");
      setContent(template.content || "");
    }
  }, [template]);

  const onSave = async () => {
    const payload: any = {
      name,
      template_type: templateType,
      subject: subject || undefined,
      provider_template_code: providerCode || undefined,
      delayed_send_minutes:
        delayedMinutes === "" ? undefined : Number(delayedMinutes),
      content,
    };
    try {
      const params = JSON.parse(placeholderParamsText || "{}");
      payload.placeholder_params = params;
    } catch {}

    const updated = await updateMutation.mutateAsync({ id, data: payload });
    navigate(`/templates/${updated.id}`);
  };

  const renderPreview = async () => {
    try {
      setPreviewError(null);
      setPreviewLoading(true);
      const raw = content || "";

      let conversationKey = "";
      let sessionId = "";
      try {
        const globalCfg = JSON.parse(
          localStorage.getItem("globalTestingConfig") || "{}",
        );
        conversationKey =
          (globalCfg && typeof globalCfg === "object"
            ? (globalCfg as any).conversation_key
            : "") || "";
        sessionId =
          (globalCfg && typeof globalCfg === "object"
            ? (globalCfg as any).session_id
            : "") || "";
      } catch {}
      conversationKey = (conversationKey || "").trim();
      sessionId = (sessionId || "").trim();
      if (!conversationKey) {
        setPreviewError(
          "Conversation Key is required for preview (set it in Testing Configuration).",
        );
        setPreviewLoading(false);
        return;
      }

      const response = await fetch(
        "https://chatbot-api.happysmoke-4e8cbf49.eastus.azurecontainerapps.io/render-template",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            conversation_key: conversationKey,
            session_id: sessionId || undefined,
            message: raw,
          }),
        },
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}) as any);
        throw new Error((err as any)?.detail || "Failed to render template");
      }

      const data = await response.json();
      setMessagePreview((data as any).rendered || "");
    } catch (e: any) {
      setPreviewError(e?.message || "Failed to preview message");
    } finally {
      setPreviewLoading(false);
    }
  };

  if (isLoading) return <div className="p-8">Loading…</div>;
  if (isError || !template) return <div className="p-8">Failed to load</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Edit Template</h1>
        <div className="flex gap-2">
          <button
            className="px-4 py-2 bg-gray-100 rounded"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded"
            onClick={onSave}
            disabled={updateMutation.isPending}
          >
            Save
          </button>
        </div>
      </div>

      {/* Details */}
      <div className="bg-white p-4 rounded shadow space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="Name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              variant="rounded-md"
            />
          </div>
          <div>
            <Select
              label="Type"
              value={templateType}
              onChange={(e) => setTemplateType(e.target.value)}
              variant="rounded-md"
            >
              <option value="email">email</option>
              <option value="sms">sms</option>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="Subject (email)"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              variant="rounded-md"
            />
          </div>
          <div>
            <Input
              label="Provider Template Code"
              type="text"
              value={providerCode}
              onChange={(e) => setProviderCode(e.target.value)}
              variant="rounded-md"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Textarea
              label="Placeholder Params (JSON)"
              className="font-mono"
              rows={6}
              value={placeholderParamsText}
              onChange={(e) => setPlaceholderParamsText(e.target.value)}
              variant="rounded-md"
            />
          </div>
          <div>
            <Input
              label="Delayed Send Minutes"
              type="number"
              value={delayedMinutes}
              onChange={(e) =>
                setDelayedMinutes(e.target.value ? Number(e.target.value) : "")
              }
              variant="rounded-md"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Content
            </label>
            <div className="flex items-center gap-2 text-sm">
              <span>Syntax:</span>
              <select
                className="px-2 py-1 border rounded"
                value={contentSyntax}
                onChange={(e) => setContentSyntax(e.target.value as any)}
              >
                <option value="markdown">Markdown</option>
                <option value="html">HTML</option>
              </select>
            </div>
          </div>
          {contentSyntax === "markdown" ? (
            <MDEditor
              value={content}
              onChange={(v = "") => setContent(v)}
              height={300}
            />
          ) : (
            <Textarea
              className="font-mono"
              rows={12}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              variant="rounded-md"
            />
          )}
        </div>
      </div>

      {/* Preview */}
      <div className="bg-white p-4 rounded shadow">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-600">Preview</span>
          <button
            className={`${previewLoading ? "bg-gray-100 text-gray-400" : "bg-blue-600 text-white hover:bg-blue-700"} px-2 py-1 text-xs rounded`}
            onClick={renderPreview}
            disabled={!!previewLoading}
          >
            {previewLoading ? "Rendering…" : "Render Preview"}
          </button>
        </div>
        {previewError && (
          <div className="text-[11px] text-red-600 mb-2">{previewError}</div>
        )}
        {messagePreview !== undefined &&
          (contentSyntax === "markdown" ? (
            <MarkdownPreview source={messagePreview} />
          ) : (
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: messagePreview }}
            />
          ))}
      </div>
      {template?.id ? (
        <AuditInfo
          resource="templates"
          id={template.id}
          fetch={auditApi.getLastChange}
        />
      ) : null}
    </div>
  );
}
